/*
  aness-product-reviews.js — original, minimal, scoped interactivity for the
  Aness Product Reviews section (Atelier-based theme). No external dependencies.
  V1 local-only behaviour:
    - FILTERS: toggle a panel and filter / sort the already-rendered review items.
        all              -> show everything (restores original order)
        rating:5|4|3     -> data-rating exact match
        photos           -> data-has-photo="true"
        verified         -> data-verified="true"
        age:18-24 ...    -> data-age exact match (empty age never matches)
        size:XS|S|M|L    -> data-size exact match (empty size never matches)
        helpful          -> show all, SORTED by data-likes descending (DOM reorder)
      No reload, no network. Empty result -> "No reviews match this filter."
    - LIKE: increment / decrement a visual counter, persisted in localStorage,
      keyed by product + section + review block id.
  Implemented as a custom element so it re-initialises on shopify:section:load.
*/
(function () {
  if (window.customElements && customElements.get('aness-product-reviews')) {
    return;
  }

  function safeStorage() {
    try {
      var probe = '__aness_probe__';
      window.localStorage.setItem(probe, probe);
      window.localStorage.removeItem(probe);
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function likesOf(item) {
    var n = parseInt(item.getAttribute('data-likes'), 10);
    return isNaN(n) ? 0 : n;
  }

  class AnessProductReviews extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;
      this.classList.add('aness-js');
      this._initFilters();
      this._initLikes();
    }

    _initFilters() {
      var toggle = this.querySelector('[data-reviews-filter-toggle]');
      var panel = this.querySelector('[data-reviews-filter-panel]');
      var buttons = Array.prototype.slice.call(this.querySelectorAll('[data-filter]'));
      var list = this.querySelector('[data-reviews-list]');
      var items = Array.prototype.slice.call(this.querySelectorAll('[data-reviews-item]'));
      var empty = this.querySelector('[data-reviews-empty]');
      var originalOrder = items.slice();

      if (toggle && panel) {
        toggle.addEventListener('click', function () {
          var open = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!open));
          panel.hidden = open;
        });
      }

      if (!buttons.length || !items.length || !list) return;

      function restoreOrder() {
        originalOrder.forEach(function (it) { list.appendChild(it); });
      }

      function matches(item, type, value) {
        if (type === 'all') return true;
        if (type === 'rating') return item.getAttribute('data-rating') === value;
        if (type === 'photos') return item.getAttribute('data-has-photo') === 'true';
        if (type === 'verified') return item.getAttribute('data-verified') === 'true';
        if (type === 'age') {
          var age = item.getAttribute('data-age');
          return age !== '' && age === value;
        }
        if (type === 'size') {
          var size = item.getAttribute('data-size');
          return size !== '' && size === value;
        }
        return true;
      }

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var raw = btn.getAttribute('data-filter') || 'all';
          var parts = raw.split(':');
          var type = parts[0];
          var value = parts[1] || '';

          buttons.forEach(function (b) {
            b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
          });

          var visible = 0;

          if (type === 'helpful') {
            // Sort: show all reviews, ordered by base like count (desc).
            var sorted = items.slice().sort(function (a, b) {
              return likesOf(b) - likesOf(a);
            });
            sorted.forEach(function (it) {
              it.hidden = false;
              list.appendChild(it);
            });
            visible = items.length;
          } else {
            restoreOrder();
            items.forEach(function (it) {
              var show = matches(it, type, value);
              it.hidden = !show;
              if (show) visible += 1;
            });
          }

          if (empty) empty.hidden = visible !== 0;
        });
      });
    }

    _initLikes() {
      var store = safeStorage();
      var likes = Array.prototype.slice.call(this.querySelectorAll('[data-reviews-like]'));
      if (!likes.length) return;

      likes.forEach(function (btn) {
        var base = parseInt(btn.getAttribute('data-base-count'), 10);
        if (isNaN(base)) base = 0;
        var key = btn.getAttribute('data-like-key');
        var countEl = btn.querySelector('[data-like-count]');
        var liked = false;

        if (store && key) {
          try { liked = store.getItem(key) === '1'; } catch (e) { liked = false; }
        }

        function render() {
          btn.setAttribute('aria-pressed', String(liked));
          btn.classList.toggle('is-liked', liked);
          if (countEl) countEl.textContent = base + (liked ? 1 : 0);
        }

        render();

        btn.addEventListener('click', function () {
          liked = !liked;
          if (store && key) {
            try {
              if (liked) { store.setItem(key, '1'); }
              else { store.removeItem(key); }
            } catch (e) { /* storage unavailable: keep in-memory state only */ }
          }
          render();
        });
      });
    }
  }

  if (window.customElements) {
    customElements.define('aness-product-reviews', AnessProductReviews);
  }
})();
